import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../supabaseClient";
import AcceptVendorModal from "./AcceptVendorModal";
import VendorDetailsModal from "./VendorDetailsModal";
import {
  IconTrash,
  IconPencil,
  IconChevronRight,
  IconCheckCircle,
  IconClock,
  IconAlertTriangle,
  IconMinusCircle,
} from "./VendorIcons";
import "./VendorCard.css";

const VAT_RATE        = 0.16;
const POSITIONING_FEE = 7000;

const EXTRA_PRICES = {
  "Hay Bales":     431.03,
  "Wristbands":    258.62,
  "Single Socket": 3000,
};

const TENT_PRICES = {
  "20x20_single": 90000,
  "20x20_shared": 55000,
  "10x10_single": 60000,
  "food_court":   95000,
  "pod_shared":   30000,
  "20x20_open":   90000,
  "ITC":          0,
  "sponsored":    0,
  "WFTO":         0,
  "biz_boost": 7500,
};

const CUSTOM_PRICE_TENT_TYPES = new Set(["sponsored", "ITC"]);
const WAIVED_SINGLE_SOCKET_TENT_TYPES = new Set(["WFTO", "food_court", "20x20_single"]);

function computeInvoiceTotals({ tentType, customBasePrice, extras, hasSpot }) {
  const supportsCustomPrice = CUSTOM_PRICE_TENT_TYPES.has(tentType);
  const hasCustomPrice      = customBasePrice != null && customBasePrice > 0;

  const basePrice = supportsCustomPrice && hasCustomPrice
    ? Number(customBasePrice)
    : (TENT_PRICES[tentType] ?? 0);

  const positioningFee = hasSpot ? POSITIONING_FEE : 0;

  const extrasTotal = Object.entries(extras ?? {}).reduce((t, [key, qty]) => {
    const isWaivedSocket = key === "Single Socket" && WAIVED_SINGLE_SOCKET_TENT_TYPES.has(tentType);
    const unitPrice = isWaivedSocket ? 0 : (EXTRA_PRICES[key] || 0);
    return t + unitPrice * Number(qty || 0);
  }, 0);

  const subtotal         = basePrice + extrasTotal + positioningFee;
  const vatAmount        = subtotal * VAT_RATE;
  const total            = Math.round(subtotal + vatAmount);
  const depositRequired = Math.round(total * 0.5);

  return { total, depositRequired };
}

const normalizeStatus = (raw) => {
  if (!raw) return "pending";
  const s = String(raw).toLowerCase().trim();
  if (["approved", "accepted", "confirmed"].includes(s)) return "approved";
  if (["denied", "rejected"].includes(s)) return "denied";
  if (s === "on_hold") return "on_hold";
  return "pending";
};

const getPaymentStatus = (app = {}) => {
  if (app.balance_confirmed) {
    return { label: "Balance paid", tone: "paid", Icon: IconCheckCircle };
  }
  if (app.balance_submitted) {
    return { label: "Balance - awaiting confirmation", tone: "pending", Icon: IconClock };
  }
  if (app.deposit_confirmed) {
    return { label: "Balance due", tone: "warning", Icon: IconAlertTriangle };
  }
  if (app.deposit_submitted) {
    return { label: "Deposit — awaiting confirmation", tone: "pending", Icon: IconClock };
  }
  return { label: "Not paid", tone: "none", Icon: IconMinusCircle };
};

const formatLabel = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const extractHandle = (input, platform) => {
  if (!input) return null;
  let value = input.trim();
  const regex =
    platform === "instagram"
      ? /instagram\.com\/([^/?#]+)/i
      : /facebook\.com\/([^/?#]+)/i;
  const match = value.match(regex);
  if (match) value = match[1];
  value = value.replace(/^@/, "").replace(/\/$/, "").replace(/\s+/g, "");
  return value || null;
};

const normalizeTag = (cat) => {
  if (typeof cat === "string") return cat.replace(/^"|"$/g, "");
  if (cat && typeof cat === "object" && "label" in cat) return cat.label;
  return String(cat ?? "");
};

const TENT_OPTIONS = [
  { value: "20x20_single", label: "20ft Individual" },
  { value: "20x20_shared", label: "20ft Sharing" },
  { value: "10x10_single", label: "10ft Individual" },
  { value: "food_court",   label: "Food Court" },
  { value: "pod_shared",   label: "Pod " },
  { value: "20x20_open",   label: "20ft Open Space" },
  { value: "sponsored",    label: "Sponsored Space" },
  { value: "ITC",          label: "ITC Space" },
  { value: "WFTO",         label: "WFTO Space" },
  {Value : "biz_boost",  label:"Biz Boost"},
];

const CUSTOM_PRICE_COPY = {
  sponsored: {
    label: "Discounted base price",
    hint:  "Set the custom price for this vendor's sponsored space.",
  },
  ITC: {
    label: "ITC base price",
    hint:  "Set the custom price for this vendor's ITC space.",
  },
};

const AVATAR_PALETTE = ["#334155", "#1e3a5f", "#3f3f5c", "#0f4c4c", "#4a4e69", "#3f3f46"];

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return hash;
};

const avatarColor = (name) => (name ? AVATAR_PALETTE[hashString(name) % AVATAR_PALETTE.length] : AVATAR_PALETTE[0]);

const initials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const VendorAvatar = ({ name, logoUrl }) => {
  const [errored, setErrored] = useState(false);
  if (logoUrl && !errored) {
    return (
      <img
        className="vc-avatar-img"
        src={logoUrl}
        alt=""
        onError={() => setErrored(true)}
      />
    );
  }
  return (
    <div className="vc-avatar-fallback" style={{ background: avatarColor(name) }}>
      {initials(name)}
    </div>
  );
};

const VendorCard = ({ application = {}, vendor = {}, tentDisplay, onStatusChange, onDelete, isNewVendor = false }) => {
  const initialStatus = useMemo(
    () => normalizeStatus(application.status),
    [application.status]
  );

  const [status, setStatus]     = useState(initialStatus);
  const [deleting, setDeleting] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const paymentStatus = useMemo(() => getPaymentStatus(application), [application]);

  // ── Accept modal ──
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [accepting, setAccepting]             = useState(false);
  const [acceptError, setAcceptError]         = useState("");
  const isNewAccount = !vendor?.user_id;

  // ── Tent type editing ──
  const [editingTent, setEditingTent]   = useState(false);
  const [tentType, setTentType]         = useState(application.tent_type || "");
  const [tentSaving, setTentSaving]     = useState(false);
  const [tentSaved, setTentSaved]       = useState(false);
  const [tentError, setTentError]       = useState("");

  // ── Custom price ──
  const isDiscounted  = CUSTOM_PRICE_TENT_TYPES.has(tentType);
  const priceCopy     = CUSTOM_PRICE_COPY[tentType] || CUSTOM_PRICE_COPY.sponsored;
  const [priceInput, setPriceInput] = useState(
    application.custom_base_price != null ? String(application.custom_base_price) : ""
  );
  const [priceSaving, setPriceSaving] = useState(false);
  const [priceSaved, setPriceSaved]   = useState(false);
  const [priceError, setPriceError]   = useState("");

  const instagram = extractHandle(vendor?.instagram, "instagram");
  const facebook  = extractHandle(vendor?.facebook,  "facebook");
  const tags      = (application.categories || []).map(normalizeTag).filter(Boolean);

  // Read slug field directly from vendor
  const profileSlug = vendor?.slug;

  const changeStatus = async (newStatus) => {
    setStatus(newStatus);
    await onStatusChange?.(application.id, newStatus);
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      `Delete this application from ${vendor?.brand_name || "this vendor"}? This cannot be undone.`
    );
    if (!confirmed) return;

    setDeleting(true);
    await onDelete?.(application.id);
    setDeleting(false);
  };

  const handleConfirmAccept = async () => {
    setAccepting(true);
    setAcceptError("");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated as admin.");
      const res = await fetch("/api/accept-vendor", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ applicationId: application.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to accept vendor");
      setStatus("approved");
      setShowAcceptModal(false);
      onStatusChange?.(application.id, "approved");
    } catch (err) {
      setAcceptError(err.message);
    } finally {
      setAccepting(false);
    }
  };

  const handleSaveTentType = async () => {
    if (!tentType) { setTentError("Select a space type."); return; }
    setTentError("");
    setTentSaving(true);

    const isSameType = tentType === application.tent_type;
    const nextCustomPrice = (isSameType && CUSTOM_PRICE_TENT_TYPES.has(tentType))
      ? application.custom_base_price
      : null;

    const { total, depositRequired } = computeInvoiceTotals({
      tentType,
      customBasePrice: nextCustomPrice,
      extras: application.extras,
      hasSpot: !!application.chosen_spot,
    });

    const { error } = await supabase
      .from("vendor_applications")
      .update({
        tent_type:         tentType,
        custom_base_price: nextCustomPrice,
        invoice_total:      total,
        deposit_required:   depositRequired,
      })
      .eq("id", application.id);

    setTentSaving(false);
    if (error) {
      setTentError("Failed to save. Try again.");
    } else {
      setTentSaved(true);
      setEditingTent(false);
      setPriceInput(nextCustomPrice != null ? String(nextCustomPrice) : "");
      setTimeout(() => setTentSaved(false), 2500);
    }
  };

  const handleSavePrice = async () => {
    const parsed = parseFloat(priceInput);
    if (isNaN(parsed) || parsed < 0) { setPriceError("Enter a valid amount (e.g. 25000)"); return; }
    setPriceError("");
    setPriceSaving(true);

    const { total, depositRequired } = computeInvoiceTotals({
      tentType,
      customBasePrice: parsed,
      extras: application.extras,
      hasSpot: !!application.chosen_spot,
    });

    const { error } = await supabase
      .from("vendor_applications")
      .update({
        custom_base_price: parsed,
        invoice_total:      total,
        deposit_required:   depositRequired,
      })
      .eq("id", application.id);

    setPriceSaving(false);
    if (error) {
      setPriceError("Failed to save. Try again.");
    } else {
      setPriceSaved(true);
      setTimeout(() => setPriceSaved(false), 2500);
    }
  };

  const currentTentLabel = TENT_OPTIONS.find(o => o.value === tentType)?.label || tentDisplay || tentType;
  const PaymentIcon = paymentStatus.Icon;

  return (
    <div className={`vc-card vc-card--${status} ${isNewVendor ? "vc-card--new" : ""}`}>

      {/* ── Header ── */}
      <div className="vc-header">
        <div className="vc-identity">
          {profileSlug ? (
            <a
              href={`/v/${profileSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="vc-avatar-link"
              title="View vendor profile"
            >
              <VendorAvatar name={vendor?.brand_name} logoUrl={vendor?.logo_url} />
            </a>
          ) : (
            <VendorAvatar name={vendor?.brand_name} logoUrl={vendor?.logo_url} />
          )}

          <div className="vc-identity-text">
            <div className="vc-name-row">
              {profileSlug ? (
                <a
                  href={`/v/${profileSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vc-brand-link"
                  title="View vendor profile"
                >
                  <h2 className="vc-brand">{vendor?.brand_name || "Unnamed vendor"}</h2>
                </a>
              ) : (
                <h2 className="vc-brand">{vendor?.brand_name || "Unnamed vendor"}</h2>
              )}
              {isNewVendor && <span className="vc-chip vc-chip--new">New</span>}
            </div>
          </div>
        </div>

        <div className="vc-header-status">
          <span className={`vc-status-badge vc-status-badge--${status}`}>
            {status === "on_hold" ? "On hold" : formatLabel(status)}
          </span>
          <div
            className={`vc-payment-status vc-payment-status--${paymentStatus.tone}`}
            title="Deposit/balance status — check before denying"
          >
            <PaymentIcon width={13} height={13} />
            <span>{paymentStatus.label}</span>
          </div>
        </div>
      </div>

      {/* ── Space ── */}
      <div className="vc-tent-row">
        {editingTent ? (
          <div className="vc-tent-edit">
            <select
              className="vc-select"
              value={tentType}
              onChange={e => { setTentType(e.target.value); setTentError(""); }}
              autoFocus
            >
              <option value="" disabled>Select space type…</option>
              {TENT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="vc-tent-edit-actions">
              <button className="vc-btn vc-btn--primary vc-btn--sm" onClick={handleSaveTentType} disabled={tentSaving}>
                {tentSaving ? "Saving…" : "Save"}
              </button>
              <button
                className="vc-btn vc-btn--ghost vc-btn--sm"
                onClick={() => { setEditingTent(false); setTentType(application.tent_type || ""); setTentError(""); }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="vc-tent-row-inner">
            <span className="vc-space-pill">{currentTentLabel}</span>
            <button className="vc-icon-btn" onClick={() => setEditingTent(true)} aria-label="Change space type" title="Change space type">
              <IconPencil width={13} height={13} />
            </button>
            {tentSaved && <span className="vc-saved-tag">Updated</span>}
          </div>
        )}
        {tentError && <p className="vc-field-error">{tentError}</p>}
      </div>

      <div className="vc-divider" />

      {/* ── Description & pricing ── */}
      <div className="vc-body">
        <p className="vc-description">{application.description || "No description provided."}</p>

        {isDiscounted && (
          <div className="vc-field">
            <span className="vc-field-label">{priceCopy.label}</span>
            <div className="vc-price-input-row">
              <span className="vc-price-currency">KES</span>
              <input
                className={`vc-price-input ${priceError ? "vc-price-input--error" : ""}`}
                type="number"
                min="0"
                step="500"
                placeholder="e.g. 25000"
                value={priceInput}
                onChange={(e) => { setPriceInput(e.target.value); setPriceError(""); setPriceSaved(false); }}
              />
              <button
                className={`vc-btn vc-btn--sm ${priceSaved ? "vc-btn--success" : "vc-btn--primary"}`}
                onClick={handleSavePrice}
                disabled={priceSaving || !priceInput}
              >
                {priceSaving ? "Saving…" : priceSaved ? "Saved" : "Save"}
              </button>
            </div>
            {priceError && <p className="vc-field-error">{priceError}</p>}
          </div>
        )}

        {tags.length > 0 && (
          <div className="vc-tags">
            {tags.map((tag) => (
              <span key={tag} className="vc-tag-chip">{tag}</span>
            ))}
          </div>
        )}
      </div>

      <div className="vc-divider" />

      {/* ── Footer ── */}
      <div className="vc-footer">
        <div className="vc-segmented" role="group" aria-label="Application status">
          <button
            className={`vc-segment vc-segment--approved ${status === "approved" ? "is-active" : ""}`}
            onClick={() => setShowAcceptModal(true)}
            disabled={status === "approved"}
          >
            Approve
          </button>
          <button
            className={`vc-segment vc-segment--pending ${status === "pending" ? "is-active" : ""}`}
            onClick={() => changeStatus("pending")}
          >
            Pending
          </button>
          <button
            className={`vc-segment vc-segment--denied ${status === "denied" ? "is-active" : ""}`}
            onClick={() => changeStatus("denied")}
          >
            Deny
          </button>
        </div>

        <div className="vc-footer-utility">
          <button className="vc-link-btn" onClick={() => setShowDetailsModal(true)}>
            View details
            <IconChevronRight width={14} height={14} />
          </button>
          <button
            className="vc-icon-btn vc-icon-btn--danger"
            onClick={handleDelete}
            disabled={deleting}
            title="Delete this application (permanent)"
            aria-label="Delete application"
          >
            <IconTrash width={15} height={15} />
          </button>
        </div>
      </div>

      {showAcceptModal && createPortal(
        <AcceptVendorModal
          brandName={vendor?.brand_name || "this vendor"}
          isNewAccount={isNewAccount}
          loading={accepting}
          error={acceptError}
          onConfirm={handleConfirmAccept}
          onCancel={() => { setShowAcceptModal(false); setAcceptError(""); }}
        />,
        document.body
      )}

      {showDetailsModal && (
        <VendorDetailsModal
          vendor={vendor}
          application={application}
          instagram={instagram}
          facebook={facebook}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
    </div>
  );
};

export default VendorCard;